import { useState, useMemo, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Plus, Search, AlertCircle, Clock, CalendarClock, CheckCircle2, Circle, CheckCircle, CheckSquare2, LayoutList, Kanban } from "lucide-react"
import { LoadMoreButton } from "@/components/load-more-button"
import { ExportButton } from "@/components/export-button"
import { useTasks, useTaskStats, useCompleteTask, useReopenTask } from "@/hooks/use-tasks"
import { useViewPreference } from "@/hooks/use-view-preference"
import { TaskKanbanBoard } from "./task-kanban-board"
import { useTaskCategories } from "@/hooks/use-task-categories"
import { useColumnPreferences } from "@/hooks/use-column-preferences"
import { useFilterParams } from "@/hooks/use-filter-params"
import { useBulkDelete, useBulkUpdate } from "@/hooks/use-bulk-actions"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { EmptyState } from "@/components/empty-state"
import { ApiErrorState } from "@/components/api-error-state"
import { ColumnSelector, type ColumnOption } from "@/components/column-selector"
import { SortableHeader } from "@/components/sortable-header"
import { BulkActionBar, buildDeleteAction, buildAssignAction, buildStatusAction, buildPriorityAction } from "@/components/bulk-action-bar"
import { BulkDeleteDialog } from "@/components/bulk-delete-dialog"
import { BulkAssignDialog } from "@/components/bulk-assign-dialog"
import { BulkStatusDialog } from "@/components/bulk-status-dialog"
import { FilterPanel, SelectFilter, DateRangeFilter } from "@/components/filters"
import { createSelectColumn } from "@/lib/bulk-selection"
import { formatDate, sortingStateToParam, buildDetailUrl } from "@/lib/utils"
import { toast } from "sonner"
import {
  type SortingState,
  type RowSelectionState,
  type ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import type { TaskItem } from "@/lib/types"

const TASK_FILTER_KEYS = [
  "status", "priority", "assignee_id", "due_date_from", "due_date_to", "task_category_id",
] as const

const columnHelper = createColumnHelper<TaskItem>()

const PRIORITY_COLORS: Record<string, string> = {
  low: "#6B7280",
  normal: "var(--primary)",
  high: "#F59E0B",
  urgent: "#EF4444",
}
const PRIORITY_LABELS: Record<string, string> = {
  low: "Niski",
  normal: "Normalny",
  high: "Wysoki",
  urgent: "Pilny",
}
const STATUS_LABELS: Record<string, string> = {
  open: "Otwarte",
  in_progress: "W toku",
  completed: "Ukończone",
  cancelled: "Anulowane",
}

const TASK_COLUMN_IDS = ["complete", "title", "category", "priority", "due_date", "assignee", "status"]
const DEFAULT_VISIBLE = ["complete", "title", "category", "priority", "due_date", "assignee", "status"]

const COLUMN_LABELS: Record<string, string> = {
  title: "Zadanie",
  category: "Kategoria",
  priority: "Priorytet",
  due_date: "Termin",
  assignee: "Przypisany",
  status: "Status",
}

const columnOptions: ColumnOption[] = Object.keys(COLUMN_LABELS).map((id) => ({
  id,
  label: COLUMN_LABELS[id],
}))

export function TasksListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [view, setView] = useViewPreference("tasks", "list")
  const [search, setSearch] = useState("")
  const [sorting, setSorting] = useState<SortingState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showAssignDialog, setShowAssignDialog] = useState(false)
  const [showStatusDialog, setShowStatusDialog] = useState(false)
  const [showPriorityDialog, setShowPriorityDialog] = useState(false)

  const { data: categoriesData } = useTaskCategories()
  const taskCategories = categoriesData?.data ?? []

  const [cursor, setCursor] = useState<string | undefined>()
  const [allTasks, setAllTasks] = useState<TaskItem[]>([])
  const prevKeyRef = useRef("")

  const { filters, setFilter, clearFilters, activeFilterCount } = useFilterParams(TASK_FILTER_KEYS)

  const sortParam = sortingStateToParam(sorting)
  const { data, isLoading, isFetching, isError, error, refetch } = useTasks({
    search: search || undefined,
    sort: sortParam,
    cursor,
    ...filters,
  })

  const filterKey = JSON.stringify({ search, sort: sortParam, ...filters })
  useEffect(() => {
    if (filterKey !== prevKeyRef.current) {
      prevKeyRef.current = filterKey
      setCursor(undefined)
      setAllTasks([])
    }
  }, [filterKey])

  useEffect(() => {
    if (data?.data) {
      if (cursor) {
        setAllTasks((prev) => [...prev, ...data.data])
      } else {
        setAllTasks(data.data)
      }
    }
  }, [data])

  const { data: statsData } = useTaskStats()
  const completeMutation = useCompleteTask()
  const reopenMutation = useReopenTask()

  const bulkDeleteMutation = useBulkDelete("tasks")
  const bulkUpdateMutation = useBulkUpdate("tasks")

  const tasks = allTasks
  const stats = statsData?.data
  const selectedIds = Object.keys(rowSelection).filter((k) => rowSelection[k])
  const selectedCount = selectedIds.length

  const handleToggleComplete = async (e: React.MouseEvent, task: TaskItem) => {
    e.stopPropagation()
    try {
      if (task.status === "completed") {
        await reopenMutation.mutateAsync(task.id)
        toast.success("Zadanie otwarte ponownie")
      } else {
        await completeMutation.mutateAsync(task.id)
        toast.success("Zadanie ukończone")
      }
    } catch {
      toast.error("Nie udało się zmienić statusu")
    }
  }

  const columns = useMemo(
    () => [
      createSelectColumn<TaskItem>(),
      columnHelper.display({
        id: "complete",
        header: () => "",
        cell: (info) => {
          const isCompleted = info.row.original.status === "completed"
          return (
            <button
              onClick={(e) => handleToggleComplete(e, info.row.original)}
              className="rounded-full p-0.5 transition-colors hover:bg-muted"
            >
              {isCompleted ? (
                <CheckCircle size={18} className="text-green-500" />
              ) : (
                <Circle size={18} className="text-muted-foreground" />
              )}
            </button>
          )
        },
        size: 40,
      }),
      columnHelper.accessor("title", {
        header: ({ column }) => <SortableHeader column={column}>Zadanie</SortableHeader>,
        cell: (info) => {
          const task = info.row.original
          const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== "completed"
          return (
            <div>
              <span className={`font-medium ${task.status === "completed" ? "line-through text-muted-foreground" : "text-foreground"}`}>
                {info.getValue()}
              </span>
              {task.entity_links.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {task.entity_links.map(l => l.entity_name).join(", ")}
                </p>
              )}
              {task.checklist_progress.total > 0 && (
                <p className="text-xs text-muted-foreground">
                  ☑ {task.checklist_progress.completed}/{task.checklist_progress.total}
                </p>
              )}
              {isOverdue && (
                <span className="text-xs font-medium text-red-500">Zaległe</span>
              )}
            </div>
          )
        },
      }),
      columnHelper.accessor("task_category", {
        id: "category",
        header: () => "Kategoria",
        enableSorting: false,
        cell: (info) => {
          const cat = info.getValue()
          if (!cat) return <span className="text-sm text-muted-foreground">—</span>
          return (
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium"
              style={{
                backgroundColor: cat.color + "18",
                color: cat.color,
              }}
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: cat.color }}
              />
              {cat.name}
            </span>
          )
        },
      }),
      columnHelper.accessor("priority", {
        header: ({ column }) => <SortableHeader column={column}>{t("tasks.priority")}</SortableHeader>,
        cell: (info) => (
          <Badge
            variant="outline"
            className="text-xs"
            style={{ borderColor: PRIORITY_COLORS[info.getValue()], color: PRIORITY_COLORS[info.getValue()] }}
          >
            {PRIORITY_LABELS[info.getValue()]}
          </Badge>
        ),
      }),
      columnHelper.accessor("due_date", {
        header: ({ column }) => <SortableHeader column={column}>{t("tasks.dueDate")}</SortableHeader>,
        cell: (info) => {
          const date = info.getValue()
          const time = info.row.original.due_time
          if (!date) return <span className="text-sm text-muted-foreground">—</span>
          const isOverdue = new Date(date) < new Date() && info.row.original.status !== "completed"
          return (
            <span className={`text-sm ${isOverdue ? "text-red-500" : "text-muted-foreground"}`}>
              {formatDate(date)}
              {time && <span className="text-xs ml-1">{time}</span>}
            </span>
          )
        },
      }),
      columnHelper.accessor("assignee", {
        header: () => t("tasks.assignee"),
        enableSorting: false,
        cell: (info) => {
          const assignee = info.getValue()
          return (
            <span className="text-sm text-muted-foreground">
              {assignee ? `${assignee.first_name} ${assignee.last_name}` : "—"}
            </span>
          )
        },
      }),
      columnHelper.accessor("status", {
        header: ({ column }) => <SortableHeader column={column}>{t("tasks.status")}</SortableHeader>,
        cell: (info) => (
          <span className="text-xs text-muted-foreground">
            {STATUS_LABELS[info.getValue()]}
          </span>
        ),
      }),
    ],
    [t]
  )

  const {
    columnVisibility,
    setColumnVisibility,
    visibleCount,
    totalCount,
    toggleColumn,
    showAll,
    hideAll,
    isColumnVisible,
  } = useColumnPreferences("tasks", TASK_COLUMN_IDS, DEFAULT_VISIBLE)

  const handleBulkDelete = async () => {
    const result = await bulkDeleteMutation.mutateAsync({ ids: selectedIds })
    toast.success(`Usunieto ${result.deleted} zadan`)
    setRowSelection({})
  }

  const handleBulkAssign = async (userId: string) => {
    const result = await bulkUpdateMutation.mutateAsync({
      ids: selectedIds,
      changes: { assignee_id: userId },
    })
    toast.success(`Zaktualizowano ${result.updated} zadan`)
    setRowSelection({})
  }

  const handleBulkStatus = async (status: string) => {
    const result = await bulkUpdateMutation.mutateAsync({
      ids: selectedIds,
      changes: { status },
    })
    toast.success(`Zaktualizowano ${result.updated} zadan`)
    setRowSelection({})
  }

  const handleBulkPriority = async (priority: string) => {
    const result = await bulkUpdateMutation.mutateAsync({
      ids: selectedIds,
      changes: { priority },
    })
    toast.success(`Zaktualizowano ${result.updated} zadan`)
    setRowSelection({})
  }

  const bulkActions = [
    buildAssignAction(() => setShowAssignDialog(true), "Przypisz"),
    buildStatusAction(() => setShowStatusDialog(true)),
    buildPriorityAction(() => setShowPriorityDialog(true)),
    buildDeleteAction(() => setShowDeleteDialog(true)),
  ]

  const table = useReactTable({
    data: tasks,
    columns: columns as ColumnDef<TaskItem, unknown>[],
    state: { sorting, columnVisibility, rowSelection },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    getRowId: (row) => row.id,
  })

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="page-title">{t("tasks.title")}</h1>
          {/* View toggle */}
          <div className="flex items-center rounded-md border border-input overflow-hidden">
            <button
              onClick={() => setView("list")}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium transition-colors ${
                view === "list"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground"
              }`}
            >
              <LayoutList size={14} />
              {t("tasks.listView")}
            </button>
            <button
              onClick={() => setView("kanban")}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium transition-colors ${
                view === "kanban"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground"
              }`}
            >
              <Kanban size={14} />
              {t("tasks.boardView")}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            entity="tasks"
            filters={{ search, ...filters }}
            visibleColumns={Object.keys(columnVisibility).filter((k) => columnVisibility[k] !== false)}
          />
          <Button onClick={() => navigate("/tasks/new")}>
            <Plus size={16} /> {t("tasks.new")}
          </Button>
        </div>
      </div>

      {/* Stats bar */}
      {stats && (
        <div className="grid grid-cols-4 gap-3">
          <button
            onClick={() => setFilter("status", "open")}
            className="surface-card flex items-center gap-3 p-3 text-left transition-shadow hover:shadow-md"
          >
            <AlertCircle size={18} className="text-red-500" />
            <div>
              <p className="text-xs text-muted-foreground">{t("tasks.overdue")}</p>
              <p className={`tabular-nums text-lg font-semibold ${stats.overdue > 0 ? "text-red-500" : "text-foreground"}`}>
                {stats.overdue}
              </p>
            </div>
          </button>
          <button
            onClick={() => setFilter("status", "open")}
            className="surface-card flex items-center gap-3 p-3 text-left transition-shadow hover:shadow-md"
          >
            <Clock size={18} className="text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">{t("tasks.today")}</p>
              <p className="tabular-nums text-lg font-semibold text-foreground">{stats.today}</p>
            </div>
          </button>
          <button
            onClick={() => setFilter("status", "open")}
            className="surface-card flex items-center gap-3 p-3 text-left transition-shadow hover:shadow-md"
          >
            <CalendarClock size={18} className="text-amber-500" />
            <div>
              <p className="text-xs text-muted-foreground">{t("tasks.upcoming")}</p>
              <p className="tabular-nums text-lg font-semibold text-foreground">{stats.upcoming}</p>
            </div>
          </button>
          <button
            onClick={() => setFilter("status", "completed")}
            className="surface-card flex items-center gap-3 p-3 text-left transition-shadow hover:shadow-md"
          >
            <CheckCircle2 size={18} className="text-green-500" />
            <div>
              <p className="text-xs text-muted-foreground">{t("tasks.completed")}</p>
              <p className="tabular-nums text-lg font-semibold text-foreground">{stats.completed_this_week}</p>
            </div>
          </button>
        </div>
      )}

      {/* Search + Column selector */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("common.search")}
            className="h-9 w-full rounded-lg border border-input bg-transparent pl-9 pr-3 text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
        {view === "list" && (
          <ColumnSelector
            columns={columnOptions}
            visibleCount={visibleCount}
            totalCount={totalCount}
            isColumnVisible={isColumnVisible}
            onToggleColumn={toggleColumn}
            onShowAll={showAll}
            onHideAll={hideAll}
          />
        )}
      </div>

      {/* Advanced Filters */}
      <FilterPanel activeCount={activeFilterCount} onClear={clearFilters}>
        <SelectFilter
          label="Status"
          value={filters.status || ""}
          onChange={(v) => setFilter("status", v)}
          options={[
            { value: "open", label: "Otwarte" },
            { value: "completed", label: "Ukonczone" },
          ]}
          placeholder="Wszystkie"
        />
        <SelectFilter
          label="Priorytet"
          value={filters.priority || ""}
          onChange={(v) => setFilter("priority", v)}
          options={[
            { value: "urgent", label: "Pilny" },
            { value: "high", label: "Wysoki" },
            { value: "normal", label: "Normalny" },
            { value: "low", label: "Niski" },
          ]}
          placeholder="Wszystkie priorytety"
        />
        <SelectFilter
          label="Kategoria"
          value={filters.task_category_id || ""}
          onChange={(v) => setFilter("task_category_id", v)}
          options={taskCategories.map((c) => ({ value: c.id, label: c.name }))}
          placeholder="Wszystkie kategorie"
        />
        <DateRangeFilter
          label="Termin"
          fromValue={filters.due_date_from || ""}
          toValue={filters.due_date_to || ""}
          onFromChange={(v) => setFilter("due_date_from", v)}
          onToChange={(v) => setFilter("due_date_to", v)}
        />
      </FilterPanel>

      {view === "kanban" ? (
        /* Kanban Board */
        isLoading ? (
          <div className="flex gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex-1 min-w-[260px]">
                <Skeleton className="h-10 w-full mb-3" />
                <div className="space-y-2">
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-24 w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <TaskKanbanBoard tasks={tasks} />
            <LoadMoreButton
              hasMore={data?.meta.has_more ?? false}
              isLoading={isFetching && !!cursor}
              onClick={() => {
                if (data?.meta.next_cursor) setCursor(data.meta.next_cursor)
              }}
            />
          </>
        )
      ) : (
        <>
          {/* Bulk action bar */}
          <BulkActionBar
            selectedCount={selectedCount}
            onDeselectAll={() => setRowSelection({})}
            actions={bulkActions}
          />

          {/* Table */}
          {isError ? (
            <ApiErrorState error={error} onRetry={() => refetch()} />
          ) : isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : tasks.length === 0 ? (
            !search && activeFilterCount === 0 ? (
              <EmptyState
                icon={CheckSquare2}
                heading="Brak zadan"
                description="Utworz swoje pierwsze zadanie"
                actionLabel="Nowe zadanie"
                onAction={() => navigate("/tasks/new")}
              />
            ) : (
              <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed py-12 text-muted-foreground">
                <p className="text-sm">{t("common.noResults")}</p>
                <Button variant="outline" onClick={() => navigate("/tasks/new")}>
                  <Plus size={16} /> {t("tasks.new")}
                </Button>
              </div>
            )
          ) : (
            <div className="surface-card overflow-hidden">
              <table className="w-full">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="bg-muted/30">
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground"
                          style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => navigate(buildDetailUrl("/tasks", row.original.id, { sort: sortingStateToParam(sorting), filters: filters as Record<string, string | undefined> }))}
                      className="cursor-pointer border-t transition-colors hover:bg-muted/30"
                      data-state={row.getIsSelected() ? "selected" : undefined}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-4 py-3">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <LoadMoreButton
            hasMore={data?.meta.has_more ?? false}
            isLoading={isFetching && !!cursor}
            onClick={() => {
              if (data?.meta.next_cursor) setCursor(data.meta.next_cursor)
            }}
          />
        </>
      )}

      {/* Bulk action dialogs */}
      <BulkDeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        count={selectedCount}
        entityLabel="zadan"
        onConfirm={handleBulkDelete}
      />
      <BulkAssignDialog
        open={showAssignDialog}
        onOpenChange={setShowAssignDialog}
        count={selectedCount}
        onConfirm={handleBulkAssign}
        fieldLabel="Przypisany"
      />
      <BulkStatusDialog
        open={showStatusDialog}
        onOpenChange={setShowStatusDialog}
        count={selectedCount}
        options={[
          { value: "open", label: "Otwarte" },
          { value: "in_progress", label: "W toku" },
          { value: "completed", label: "Ukonczone" },
          { value: "cancelled", label: "Anulowane" },
        ]}
        onConfirm={handleBulkStatus}
      />
      <BulkStatusDialog
        open={showPriorityDialog}
        onOpenChange={setShowPriorityDialog}
        count={selectedCount}
        options={[
          { value: "urgent", label: "Pilny" },
          { value: "high", label: "Wysoki" },
          { value: "normal", label: "Normalny" },
          { value: "low", label: "Niski" },
        ]}
        onConfirm={handleBulkPriority}
        title="Zmien priorytet"
        fieldLabel="Priorytet"
      />
    </div>
  )
}
